import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Truck, MapPin, Phone, MessageCircle, CheckCircle2, XCircle, Navigation, Printer, ChevronDown, PackageCheck, Plus, X } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";
import { PdfLangSelect } from "@/components/PdfLangMenu";
import { printStopReceipt } from "@/lib/deliveryReceiptPdf";
import { openWhatsApp, trackMessage } from "@/lib/whatsapp";

const TONE = {
  entregado: "bg-emerald-100 text-emerald-700",
  en_camino: "bg-blue-100 text-blue-700",
  fallido: "bg-red-100 text-red-700",
  pendiente: "bg-slate-100 text-slate-600",
};

export default function MyRoute() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ nota: "", photo_ids: [], firma_data: "" });
  const [lang, setLang] = useState("es");
  const [busy, setBusy] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [pickCodes, setPickCodes] = useState([]);
  const [pickInput, setPickInput] = useState("");
  const [pickModo, setPickModo] = useState("terrestre");
  const [pickBusy, setPickBusy] = useState(false);
  const [pickResult, setPickResult] = useState(null);
  const [pickOpen, setPickOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/my/route"); setData(r.data); }
    catch (err) { setData({ route: null, error: err.response?.data?.detail || "" }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const route = data?.route;
  const stops = route?.stops || [];

  const setState = async (stop, estado, withReceipt = false) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/driver-routes/${route.id}/stops/${stop.id}/state`, {
        estado, nota: form.nota, photo_ids: form.photo_ids, firma_data: form.firma_data,
      });
      setData(d => ({ ...d, route: res }));
      toast.success(t("saved"));
      if (withReceipt) {
        const fresh = (res.stops || []).find(s => s.id === stop.id) || { ...stop, estado, ...form };
        await printStopReceipt({ stop: { ...fresh, firma_data: form.firma_data || fresh.firma_data }, route: res, driver: data?.driver, lang });
      }
      setForm({ nota: "", photo_ids: [], firma_data: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const toggle = (id) => { setOpen(o => (o === id ? null : id)); setForm({ nota: "", photo_ids: [], firma_data: "" }); };

  const addPickCode = () => {
    const c = pickInput.trim().toUpperCase();
    if (!c) return;
    setPickCodes(prev => (prev.includes(c) ? prev : [...prev, c]));
    setPickInput("");
  };
  const submitPickup = async () => {
    if (pickCodes.length === 0) { toast.error(t("scan_at_least_one")); return; }
    setPickBusy(true);
    try {
      const { data: res } = await api.post("/driver/pickup", {
        codes: pickCodes, route_id: route?.id || "", modo: pickModo,
      });
      setPickResult(res);
      toast.success(`${t("picked_up")}: ${res.packages_included} · ${res.total_peso} kg`);
      setPickCodes([]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setPickBusy(false); }
  };

  const optimize = async () => {
    if (!route) return;
    setOptimizing(true);
    try {
      const { data: res } = await api.post(`/driver-routes/${route.id}/optimize`, { hora_inicio: "09:00" });
      setData(d => ({ ...d, route: res }));
      toast.success(t("route_optimized"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setOptimizing(false); }
  };

  if (!data) return <div className="p-8 text-center text-slate-400">…</div>;

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="my-route-page">
      <div className="bg-[#0B0F19] text-white rounded-xl p-4">
        <div className="flex items-center gap-2 text-sm font-semibold"><Truck className="w-4 h-4 text-red-500" /> {t("my_route")}</div>
        <div className="text-xs text-slate-400 mt-1">
          {data.driver?.nombre || "—"} · {route?.vehiculo?.matricula || t("no_vehicle")} · {route?.fecha || "—"}
        </div>
        {route && (
          <div className="flex gap-4 mt-3 text-xs items-center">
            <span className="text-emerald-400 font-bold">{route.progreso?.entregados || 0} ✓</span>
            <span className="text-slate-400">{route.progreso?.pendientes || 0} ⏳</span>
            <span className="text-red-400">{route.progreso?.fallidos || 0} ✕</span>
            {route.total_km ? <span className="text-blue-300 tabular-nums ms-auto">{route.total_km} km</span> : null}
            <button
              data-testid="my-route-optimize"
              onClick={optimize}
              disabled={optimizing}
              className={`${route.total_km ? "" : "ms-auto"} flex items-center gap-1 bg-blue-600 disabled:opacity-50 text-white font-semibold px-2.5 py-1.5 rounded-lg`}
            ><Navigation className="w-3.5 h-3.5" /> {optimizing ? t("optimizing") : t("optimize_route")}</button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="pickup-panel">
        <button onClick={() => setPickOpen(o => !o)} data-testid="pickup-toggle" className="w-full text-start p-4 flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0"><PackageCheck className="w-5 h-5" /></span>
          <div className="flex-1">
            <div className="font-semibold text-sm">{t("pickup_scan")}</div>
            <div className="text-xs text-slate-500">{t("pickup_scan_hint")}</div>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${pickOpen ? "rotate-180" : ""}`} />
        </button>
        {pickOpen && (
          <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
            <div className="grid grid-cols-3 gap-2">
              {[["terrestre", t("road")], ["maritimo", t("sea")], ["aereo", t("air")]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`pickup-modo-${v}`} onClick={() => setPickModo(v)}
                  className={`py-1.5 rounded-lg text-xs font-semibold border ${pickModo === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>{l}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="pickup-input"
                value={pickInput}
                onChange={(e) => setPickInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPickCode(); } }}
                placeholder={t("scan_or_type_code")}
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-600"
              />
              <button data-testid="pickup-add" onClick={addPickCode} className="bg-slate-900 text-white px-3 rounded-lg"><Plus className="w-4 h-4" /></button>
            </div>
            {pickCodes.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="pickup-codes">
                {pickCodes.map(c => (
                  <span key={c} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 text-xs font-mono px-2 py-1 rounded">
                    {c}
                    <button data-testid={`pickup-remove-${c}`} onClick={() => setPickCodes(prev => prev.filter(x => x !== c))}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <button data-testid="pickup-submit" onClick={submitPickup} disabled={pickBusy || pickCodes.length === 0}
              className="w-full bg-emerald-600 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2">
              <PackageCheck className="w-4 h-4" /> {pickBusy ? "…" : `${t("generate_pickup_manifest")} (${pickCodes.length})`}
            </button>
            {pickResult && (
              <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1" data-testid="pickup-result">
                <div className="font-mono font-bold text-slate-900">{pickResult.manifest?.codigo}</div>
                <div className="text-slate-600">{pickResult.packages_included} pkg · {pickResult.total_bultos} bultos · {pickResult.total_peso} kg</div>
                {pickResult.not_found?.length > 0 && <div className="text-red-600">{t("not_found")}: {pickResult.not_found.join(", ")}</div>}
                {pickResult.avisos_whatsapp?.length > 0 && (
                  <div className="pt-2 space-y-1">
                    {pickResult.avisos_whatsapp.map((a) => (
                      <button
                        key={a.tracking}
                        data-testid={`pickup-wa-${a.tracking}`}
                        onClick={() => window.open(a.url, "_blank")}
                        className="w-full flex items-center gap-1.5 justify-center text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> {t("notify_pickup_wa")} · {a.tracking}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400" data-testid="no-route">
          <MapPin className="w-10 h-10 mx-auto opacity-30 mb-3" />
          <p className="text-sm">{data.error || t("no_route_today")}</p>
        </div>
      )}

      {stops.map(s => (
        <div key={s.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`my-stop-${s.id}`}>
          <button onClick={() => toggle(s.id)} data-testid={`my-stop-toggle-${s.id}`} className="w-full text-start p-4 flex items-start gap-3">
            <span className="w-7 h-7 rounded-lg bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{s.orden}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {s.eta && <span className="text-[11px] font-bold tabular-nums text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded" data-testid={`my-stop-eta-${s.id}`}>{s.eta}</span>}
                <span className="font-mono font-semibold text-sm">{s.tracking_code || "—"}</span>
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t(`stop_type_${s.tipo}`)}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${TONE[s.estado] || TONE.pendiente}`}>{t(`stop_${s.estado}`)}</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">{s.contacto || "—"}</div>
              <div className="text-xs text-slate-500 truncate">{s.direccion || "—"} {s.provincia || ""}</div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open === s.id ? "rotate-180" : ""}`} />
          </button>

          <div className="px-4 pb-3 flex gap-2 flex-wrap">
            {s.telefono && (
              <>
                <a href={`tel:${s.telefono}`} data-testid={`my-stop-call-${s.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {t("call")}</a>
                <button
                  data-testid={`my-stop-wa-${s.id}`}
                  onClick={() => openWhatsApp(s.telefono, trackMessage({ nombre: s.contacto, tracking: s.tracking_code, estado: t(`stop_${s.estado}`), url: `${window.location.origin}/track/${s.tracking_code || ""}` }))}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white flex items-center gap-1"
                ><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</button>
              </>
            )}
            {s.direccion && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${s.direccion} ${s.provincia || ""}`)}`} target="_blank" rel="noreferrer" data-testid={`my-stop-map-${s.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 flex items-center gap-1"><Navigation className="w-3.5 h-3.5" /> {t("open_maps")}</a>
            )}
            {s.estado === "entregado" && (
              <button data-testid={`my-stop-reprint-${s.id}`} onClick={() => printStopReceipt({ stop: s, route, driver: data.driver, lang })} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-900 text-white flex items-center gap-1"><Printer className="w-3.5 h-3.5" /> {t("print_receipt")}</button>
            )}
          </div>

          {open === s.id && (
            <div className="border-t border-slate-200 p-4 space-y-3 bg-slate-50" data-testid={`my-stop-panel-${s.id}`}>
              <button
                data-testid={`my-stop-onway-${s.id}`}
                disabled={busy}
                onClick={() => setState(s, "en_camino")}
                className="w-full bg-blue-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
              ><Navigation className="w-4 h-4" /> {t("mark_on_the_way")}</button>

              <PhotoUploader
                scope="shipment_evidence"
                value={form.photo_ids}
                onChange={(ids) => setForm(f => ({ ...f, photo_ids: ids }))}
                max={6}
                label={t("delivery_evidence")}
                testid={`my-stop-photos-${s.id}`}
              />

              <SignaturePad
                value={form.firma_data}
                onChange={(v) => setForm(f => ({ ...f, firma_data: v }))}
                label={t("signature_receiver")}
                testid={`my-stop-sign-${s.id}`}
              />

              <textarea
                data-testid={`my-stop-note-${s.id}`}
                value={form.nota}
                onChange={(e) => setForm(f => ({ ...f, nota: e.target.value }))}
                placeholder={t("notes")}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600"
              />

              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase text-slate-500">{t("report_language")}</span>
                <PdfLangSelect value={lang} onChange={setLang} testid={`my-stop-lang-${s.id}`} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  data-testid={`my-stop-delivered-${s.id}`}
                  disabled={busy}
                  onClick={() => setState(s, "entregado", true)}
                  className="bg-emerald-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                ><CheckCircle2 className="w-4 h-4" /> {s.tipo === "recogida" ? t("mark_picked_up") : t("mark_delivered")}</button>
                <button
                  data-testid={`my-stop-failed-${s.id}`}
                  disabled={busy}
                  onClick={() => setState(s, "fallido")}
                  className="bg-red-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                ><XCircle className="w-4 h-4" /> {t("mark_failed")}</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
